import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PRODUCT_TYPES } from '../constants';
import { formatPrice } from './index';

// Ana Renk - Koyu Yesil
const PRIMARY_COLOR = [41, 71, 45]; // #29472d
const SECONDARY_COLOR = [29, 51, 32]; // #1d3320
const LIGHT_BG = [234, 245, 236]; // #eaf5ec
const GRAY_TEXT = [100, 100, 100];
const HEADER_HEIGHT = 18;
const CONTENT_START_Y = 26;
const TABLE_MARGIN_TOP = 26;
const TABLE_MARGIN_BOTTOM = 28;

// Türkçe karakter temizleme fonksiyonu
const cleanTurkishChars = (text) => {
  return (text || '').replace(/[ğ]/g, 'g')
    .replace(/[Ğ]/g, 'G')
    .replace(/[ü]/g, 'u')
    .replace(/[Ü]/g, 'U')
    .replace(/[ş]/g, 's')
    .replace(/[Ş]/g, 'S')
    .replace(/[ı]/g, 'i')
    .replace(/[İ]/g, 'I')
    .replace(/[ö]/g, 'o')
    .replace(/[Ö]/g, 'O')
    .replace(/[ç]/g, 'c')
    .replace(/[Ç]/g, 'C');
};

/** PDF dosya adı için müşteri adını güvenli hale getirir */
const buildPdfFileName = (customerInfo, suffix = '') => {
  const rawName = customerInfo?.name || customerInfo?.customerName || '';
  const cleaned = cleanTurkishChars(rawName)
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_');
  const base = cleaned || 'Musteri';
  const suffixPart = suffix ? `_${cleanTurkishChars(suffix).replace(/\s+/g, '_')}` : '';
  return `${base}${suffixPart}.pdf`;
};

const isMobileOrTablet = () => {
  const isMobileUa = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isTouchTablet = typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1;
  return isMobileUa || isTouchTablet;
};

/**
 * AyHomePerde/musteri-listesi ile aynı yaklaşım:
 * - Mobil/tablet: doc.save(müşteriAdı.pdf)
 * - Masaüstü: önizleme + yeşil İndir
 */
const deliverPdf = (doc, fileName) => {
  const safeFileName = String(fileName || 'Musteri.pdf')
    .replace(/[^\w.\-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  const cleanFilename = safeFileName.toLowerCase().endsWith('.pdf')
    ? safeFileName
    : `${safeFileName}.pdf`;
  const safeTitle = cleanFilename.replace(/[<>&"']/g, '');
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);

  // Mobil / tablet — AyHome ile birebir
  if (isMobileOrTablet()) {
    doc.save(cleanFilename);
    window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 2000);
    return;
  }

  // Masaüstü: yeşil şerit + İndir + önizleme
  const previewWin = window.open('', '_blank');
  if (!previewWin) {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = cleanFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 2000);
    return;
  }

  previewWin.document.open();
  previewWin.document.write(`<!DOCTYPE html>
<html lang="tr">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${safeTitle}</title>
  <style>
    * { box-sizing: border-box; }
    html, body { margin: 0; height: 100%; background: #1a1a1a; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
    .topbar {
      position: sticky; top: 0; z-index: 5;
      display: flex; align-items: center; gap: 12px;
      padding: 10px 14px;
      background: #29472d;
      color: #fff;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    }
    .topbar .name {
      flex: 1 1 auto; min-width: 0;
      font-size: 13px; font-weight: 600;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .topbar button {
      flex-shrink: 0;
      border: 0; border-radius: 8px;
      padding: 10px 18px;
      font-size: 14px; font-weight: 700;
      cursor: pointer;
      background: #fff;
      color: #29472d;
    }
    .topbar button:hover { background: #eaf5ec; }
    .viewer {
      width: 100%;
      height: calc(100% - 52px);
      border: 0;
      background: #525659;
      display: block;
    }
  </style>
</head>
<body>
  <div class="topbar">
    <div class="name">${safeTitle}</div>
    <button type="button" id="btn-download">İndir</button>
  </div>
  <iframe class="viewer" src="${pdfUrl}" title="${safeTitle}"></iframe>
  <script>
    const fileName = ${JSON.stringify(cleanFilename)};
    const pdfUrl = ${JSON.stringify(pdfUrl)};
    document.title = fileName;
    document.getElementById('btn-download').addEventListener('click', function () {
      const a = document.createElement('a');
      a.href = pdfUrl;
      a.download = fileName;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    });
  </script>
</body>
</html>`);
  previewWin.document.close();
};

// Modern Header çiz — kısa şerit, marka solda, belge başlığı sağda
const drawHeader = (doc, title, subTitle = '') => {
  const headerH = subTitle ? 24 : HEADER_HEIGHT;
  doc.setFillColor(...PRIMARY_COLOR);
  doc.rect(0, 0, 210, headerH, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('UFUK PERDE', 14, 7.5);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('GOKTURK', 14, 12.5);

  if (subTitle) {
    doc.setFontSize(7.5);
    doc.text(cleanTurkishChars(subTitle).toUpperCase(), 14, 19.5);
  }

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.text(cleanTurkishChars(title), 196, subTitle ? 12.5 : 10, { align: 'right' });

  doc.setTextColor(0, 0, 0);
};

// Modern Footer — adres ve iletişim sayfanın altında
const drawFooter = (doc) => {
  const pageHeight = doc.internal.pageSize.height;
  const barH = 14;
  const barY = pageHeight - barH;

  // İnce ayırıcı çizgi
  doc.setDrawColor(...PRIMARY_COLOR);
  doc.setLineWidth(0.3);
  doc.line(14, barY - 4, 196, barY - 4);

  // Sayfa numarası (çubuğun üstünde)
  doc.setFontSize(7);
  doc.setTextColor(...GRAY_TEXT);
  const pageCount = doc.internal.getNumberOfPages();
  doc.text(
    `Sayfa ${doc.internal.getCurrentPageInfo().pageNumber} / ${pageCount}`,
    196,
    barY - 6,
    { align: 'right' }
  );

  // Alt koyu yeşil şerit — adres / iletişim
  doc.setFillColor(...PRIMARY_COLOR);
  doc.rect(0, barY, 210, barH, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text(
    'Gokturk Merkez Mah. Cami Sokak 13/a, Eyupsultan, Istanbul',
    105,
    barY + 5.5,
    { align: 'center' }
  );
  doc.setFontSize(6.5);
  doc.text(
    'Tel: +90 534 915 92 16   ·   gokturkufukperde@gmail.com',
    105,
    barY + 10.5,
    { align: 'center' }
  );

  doc.setTextColor(0, 0, 0);
};

// Metni satırlara böl
const splitTextToLines = (text, maxCharsPerLine) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach(word => {
    if ((currentLine + ' ' + word).trim().length <= maxCharsPerLine) {
      currentLine = (currentLine + ' ' + word).trim();
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });
  if (currentLine) lines.push(currentLine);
  return lines;
};

/** Atölye PDF kutusunda gösterilecek ek satırları oluşturur (not hariç) */
const buildWorkshopOrderExtraLines = (order) => {
  const items = [];

  const pushSpacer = () => {
    if (items.length > 0) items.push({ spacer: true });
  };

  if (order.productType === 'tul' && order.altEtekKatlama) {
    items.push({ text: 'Alt Etek Katlama olacak', color: 'primary' });
  }

  if (order.productType === 'kanat') {
    const hasOzel = (order.hasOzelTasarim || order.ozelProductName) && order.ozelProductName;
    if (hasOzel) {
      pushSpacer();
      let ozelLine = `Ozel Tasarim: ${order.ozelProductName}`;
      if (order.ozelProductVariant) ozelLine += ` - ${order.ozelProductVariant}`;
      items.push({ text: ozelLine, color: 'primary', bold: true });

      if (order.ozelPileModel) {
        items.push({ text: `Pile: ${order.ozelPileModel}`, color: 'primary' });
      }

      const dims = [];
      if (order.ozelWidth) dims.push(`En: ${order.ozelWidth}`);
      if (order.ozelHeight) dims.push(`Boy: ${order.ozelHeight}`);
      const dimPart = dims.length ? dims.join('  ') : '';
      const adetPart = `Adet: ${order.ozelQuantity || '1'}`;
      items.push({
        text: dimPart ? `${dimPart}  ${adetPart}` : adetPart,
        color: 'primary'
      });
    }

    const hasAstar = (order.hasAstar || order.astarProductName) && order.astarProductName;
    if (hasAstar) {
      pushSpacer();
      let astarLine = `Astar: ${order.astarProductName}`;
      if (order.astarProductVariant) astarLine += ` - ${order.astarProductVariant}`;
      items.push({ text: astarLine, color: 'black' });
    }

    const accs = Array.isArray(order.accessories) ? order.accessories.filter((a) => a.name) : [];
    if (accs.length > 0) {
      pushSpacer();
      accs.forEach((acc) => {
        const isMetre = acc.unit === 'metre';
        const detail = isMetre
          ? `${acc.metre || '1'} m x ${acc.quantity || '1'} ad`
          : `${acc.quantity || '1'} ad`;
        items.push({ text: `Aksesuar: ${acc.name} (${detail})`, color: 'black' });
      });
    }
  }

  return items.map((item) => {
    if (item.spacer) return item;
    return { ...item, text: cleanTurkishChars(item.text) };
  });
};

/** Atölye PDF kutusu yüksekliğini içeriğe göre hesaplar */
const calcWorkshopBoxHeight = (extraItems, noteLines) => {
  let lineCount = 0;
  extraItems.forEach((item) => {
    if (item.spacer) {
      lineCount += 1;
      return;
    }
    const wrapped = splitTextToLines(item.text, 42);
    lineCount += Math.min(wrapped.length, 2);
  });
  const noteLineCount = Math.min(noteLines.length, 3);
  const noteBlockHeight = noteLineCount > 0 ? (extraItems.length > 0 ? 12 : 7) + noteLineCount * 5 : 0;
  const innerHeight = 26 + lineCount * 5 + noteBlockHeight;
  return Math.max(63, 14 + innerHeight + 6);
};

// Müşteri bilgileri kutusu — kompakt, modern iki sütun düzen
const drawCustomerInfoBox = (doc, customerInfo, startY) => {
  const cleanName = cleanTurkishChars(customerInfo.name || '');
  const cleanAddress = cleanTurkishChars(customerInfo.address || '');
  const phone = customerInfo.phone1 || '-';
  const tcNo = customerInfo.tcNo || '';

  const addressLines = splitTextToLines(cleanAddress, 72);
  const addressLineCount = Math.max(1, addressLines.length);
  // Üst şerit + ad/tel satırı + adres satırları + alt boşluk
  const boxHeight = 8 + 12 + (addressLineCount * 5) + 6;

  const boxX = 15;
  const boxW = 180;

  // Açık arka plan
  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(boxX, startY, boxW, boxHeight, 2, 2, 'F');

  // Sol ince yeşil vurgu
  doc.setFillColor(...PRIMARY_COLOR);
  doc.rect(boxX, startY, 2.2, boxHeight, 'F');

  // Üst etiket + tarihler aynı satırda
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('MUSTERI BILGILERI', boxX + 7, startY + 6);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY_TEXT);
  const dateParts = [];
  if (customerInfo.orderDate) {
    const orderDate = customerInfo.orderDate?.toDate
      ? customerInfo.orderDate.toDate()
      : new Date(customerInfo.orderDate);
    dateParts.push(`Siparis: ${orderDate.toLocaleDateString('tr-TR')}`);
  }
  if (customerInfo.deliveryDate) {
    const deliveryDate = customerInfo.deliveryDate?.toDate
      ? customerInfo.deliveryDate.toDate()
      : new Date(customerInfo.deliveryDate);
    dateParts.push(`Teslimat: ${deliveryDate.toLocaleDateString('tr-TR')}`);
  }
  if (dateParts.length > 0) {
    doc.text(dateParts.join('   ·   '), boxX + boxW - 5, startY + 6, { align: 'right' });
  }

  // Ad soyad (sol) + telefon / TC (sağ)
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(cleanName || '-', boxX + 7, startY + 15);

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(40, 40, 40);
  const rightMeta = tcNo ? `${phone}   ·   TC: ${tcNo}` : phone;
  doc.text(rightMeta, boxX + boxW - 5, startY + 15, { align: 'right' });

  // Adres
  let addressY = startY + 22;
  doc.setFontSize(8);
  doc.setTextColor(...GRAY_TEXT);
  addressLines.forEach((line, index) => {
    const prefix = index === 0 ? 'Adres: ' : '';
    doc.text(`${prefix}${line}`, boxX + 7, addressY);
    addressY += 5;
  });

  doc.setTextColor(0, 0, 0);
  return startY + boxHeight + 6;
};

// Toplam tutar kutusu - Sağda alt alta format
const drawTotalBox = (doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, startY, showPaymentInfo = false, paidAmount = 0, remainingAmount = 0, showDetailedPricing = true) => {
  const vYuzde = parseFloat(vadeliYuzde) || 0;
  const gIskonto = parseFloat(genelIskontoYuzde) || 0;

  const hasVade = vYuzde > 0;
  const hasIskonto = gIskonto > 0;
  const hasProductDiscount = discountedTotalAmount !== totalAmount && discountedTotalAmount > 0;

  // Hesaplamalar
  const vadeliToplam = totalAmount + (totalAmount * vYuzde / 100);
  const iskontoluToplam = totalAmount - (totalAmount * gIskonto / 100);

  // Hiyerarşi (Genel Toplam): Vade Farkı > Genel İskonto > Ürün İndirimi > Ara Toplam
  let grandTotal = totalAmount;
  if (hasVade) grandTotal = vadeliToplam;
  else if (hasIskonto) grandTotal = iskontoluToplam;
  else if (hasProductDiscount) grandTotal = discountedTotalAmount;

  // Kutu yüksekliğini hesapla
  let itemCount = 1; // Sadece Genel toplam (alt sınır)
  if (showDetailedPricing) {
    itemCount += 1; // Ara toplam
    if (hasProductDiscount) itemCount++;
    if (hasIskonto) itemCount += 2; // % + tutar
    if (hasVade) itemCount += 2; // % + tutar
  }

  if (showPaymentInfo) {
    itemCount += 2; // Ödenen + Kalan
  }
  const boxHeight = 20 + (itemCount * 10);

  // Ana kutu
  doc.setFillColor(...PRIMARY_COLOR);
  doc.roundedRect(100, startY, 95, boxHeight, 3, 3, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  let lineY = startY + 12;

  if (showDetailedPricing) {
    // Ara Toplam (Brüt)
    doc.text('Ara Toplam:', 105, lineY);
    doc.text(`${formatPrice(totalAmount)} TL`, 190, lineY, { align: 'right' });
    lineY += 9;

    // Ürün İndirimli Toplam
    if (hasProductDiscount) {
      doc.text('Urun Indirimli Toplam:', 105, lineY);
      doc.text(`${formatPrice(discountedTotalAmount)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;
    }

    // Genel İskonto
    if (hasIskonto) {
      doc.text(`Genel Iskonto Tutari (%${genelIskontoYuzde}):`, 105, lineY);
      doc.text(`-${formatPrice(totalAmount * gIskonto / 100)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;
      
      doc.text('Iskontolu Toplam:', 105, lineY);
      doc.text(`${formatPrice(iskontoluToplam)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;
    }

    // Vade Farkı
    if (hasVade) {
      doc.text(`Vade Farki (%${vadeliYuzde}):`, 105, lineY);
      doc.text(`+${formatPrice(totalAmount * vYuzde / 100)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;

      doc.text('Vadeli Toplam:', 105, lineY);
      doc.text(`${formatPrice(vadeliToplam)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;
    }

    // Ayırıcı çizgi (sadece detaylı görünümde ara toplamlar varsa)
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.3);
    doc.line(105, lineY - 2, 190, lineY - 2);
  }


  // Genel toplam
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('GENEL TOPLAM (Net):', 105, lineY + 6);
  doc.text(`${formatPrice(grandTotal)} TL`, 190, lineY + 6, { align: 'right' });
  lineY += 14;

  // Ödenen ve Kalan bilgileri (sadece Müşteri PDF için)
  if (showPaymentInfo) {
    // Ayırıcı çizgi
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.3);
    doc.line(105, lineY - 2, 190, lineY - 2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);

    // Ödenen
    doc.text('Odenen:', 105, lineY + 4);
    doc.text(`${formatPrice(paidAmount)} TL`, 190, lineY + 4, { align: 'right' });
    lineY += 9;

    // Kalan
    const isPaymentComplete = Math.abs(remainingAmount) < 0.01 || remainingAmount <= 0;
    doc.setFont('helvetica', 'bold');
    doc.text('Kalan:', 105, lineY + 4);
    if (isPaymentComplete) {
      doc.text('ODEME TAMAMLANDI', 190, lineY + 4, { align: 'right' });
    } else {
      doc.text(`${formatPrice(remainingAmount)} TL`, 190, lineY + 4, { align: 'right' });
    }
  }

  doc.setTextColor(0, 0, 0);
  return startY + boxHeight + 10;
};

// Müşteri PDF tablo verisini oluşturur
const buildCustomerTableData = (orders, includeMeasurements = true) => {
  const head = includeMeasurements
    ? [['Urun Tipi', 'Oda', 'Urun Adi', 'Olculer(cm)', 'Pile Modeli', 'Adet', 'Toplam Ucret (TL)']]
    : [['Urun Tipi', 'Oda', 'Urun Adi', 'Pile Modeli', 'Adet', 'Toplam Ucret (TL)']];

  const body = orders.flatMap((order) => {
    const cleanProductType = cleanTurkishChars(PRODUCT_TYPES[order.productType]?.label || order.productType || '');
    const cleanRoomName = cleanTurkishChars(order.roomName || '');
    const cleanPileModel = cleanTurkishChars(order.pileModel || '');

    // Plise için özel satır bazlı genişletme
    if (order.productType === 'plise' && Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
      const p1Base = cleanTurkishChars(order.pliseProduct1Name || '');
      const p2Base = order.pliseProduct2Name ? ` + ${cleanTurkishChars(order.pliseProduct2Name)}` : '';

      return order.pliseItems.map((item) => {
        const ms = item.multisistem ? ' (Multisistem)' : '';
        let cleanProductName = item.multisistem ? (p1Base + p2Base + cleanTurkishChars(ms)) : p1Base;
        if (item.hasPratikMontaj) {
          cleanProductName += cleanTurkishChars(' (Pratik Montaj)');
        }

        const row = [
          cleanProductType,
          cleanRoomName,
          cleanProductName
        ];

        if (includeMeasurements) {
          row.push(`${item.width || '-'} x ${item.height || '-'}`);
        }

        const rowTotal = parseFloat(item.rowTotal) || 0;
        const priceText = formatPrice(rowTotal);
        const priceCell = item.hasEgimFarki
          ? {
              content: `${priceText}\n${cleanTurkishChars('Egim Farki (+%50)')}`,
              styles: { halign: 'right', valign: 'middle', fontSize: 7.5 }
            }
          : priceText;

        row.push(
          '-',
          item.quantity || '1',
          priceCell
        );

        return row;
      });
    }

    // Diğer ürün tipleri için tek satır
    let productName = order.productName || '';
    if (order.productVariant) {
      productName += ` - ${order.productVariant}`;
    }
    const cleanProductName = cleanTurkishChars(productName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');

    const subDetailsLines = [];

    // Tül Aksesuarları
    const tulAccs = order.productType === 'tul' ? (order.tulAccessories || order.accessories || order.kanatAccessories || []) : [];
    if (order.productType === 'tul' && tulAccs.length > 0) {
      const accText = tulAccs.map((a) => {
        const isMetre = a.unitType === 'metre' || a.unit === 'metre';
        const meterVal = a.metre ?? a.meter ?? '';
        const priceVal = a.total ?? a.totalPrice ?? a.price ?? 0;
        const qtyVal = a.quantity || 1;

        if (includeMeasurements) {
          const detail = isMetre
            ? `${meterVal} m x ${qtyVal} Adet`
            : `${qtyVal} Adet`;
          return `${a.name} (${detail} - ${formatPrice(priceVal)} TL)`;
        } else {
          return `${a.name} (${qtyVal} Adet - ${formatPrice(priceVal)} TL)`;
        }
      }).join(', ');
      subDetailsLines.push(cleanTurkishChars(`Aksesuarlar: ${accText}`));
    }

    // Kanat Özel Tasarım ve Aksesuarlar
    if (order.productType === 'kanat') {
      const cd = order.customDesign || (order.hasOzelTasarim ? {
        productName: order.ozelTasarimProductName || order.ozelProductName || '',
        productVariant: order.ozelTasarimProductVariant || order.ozelProductVariant || '',
        width: order.ozelTasarimWidth || order.ozelWidth || '',
        height: order.ozelTasarimHeight || order.ozelHeight || '',
        quantity: order.ozelTasarimQuantity || order.ozelQuantity || '1',
        totalPrice: order.ozelTasarimTotalPrice || order.ozelTotalPrice || order.ozelTasarimTotal || 0
      } : null);

      if (cd && cd.productName) {
        const variantText = cd.productVariant ? ` (${cd.productVariant})` : '';
        const sizeText = (includeMeasurements && (cd.width || cd.height)) ? ` | ${cd.width}x${cd.height} cm` : '';
        const priceVal = cd.totalPrice || cd.price || 0;
        const priceText = priceVal ? ` | Tutar: ${formatPrice(priceVal)} TL` : '';
        const cdText = `Ozel Tasarim: ${cd.productName}${variantText}${sizeText} | ${cd.quantity || '1'} Adet${priceText}`;
        subDetailsLines.push(cleanTurkishChars(cdText));
      }

      if (order.hasAstar && order.astarProductName) {
        const variantText = order.astarProductVariant ? ` (${order.astarProductVariant})` : '';
        const priceText = order.astarPrice ? ` | Tutar: ${formatPrice(order.astarPrice)} TL` : '';
        subDetailsLines.push(cleanTurkishChars(`Astar: ${order.astarProductName}${variantText}${priceText}`));
      }

      const kanatAccs = order.kanatAccessories || order.accessories || order.tulAccessories || [];
      if (kanatAccs.length > 0) {
        const accText = kanatAccs.map((a) => {
          const isMetre = a.unitType === 'metre' || a.unit === 'metre';
          const meterVal = a.metre ?? a.meter ?? '';
          const priceVal = a.total ?? a.totalPrice ?? a.price ?? 0;
          const qtyVal = a.quantity || 1;

          if (includeMeasurements) {
            const detail = isMetre
              ? `${meterVal} m x ${qtyVal} Adet`
              : `${qtyVal} Adet`;
            return `${a.name} (${detail} - ${formatPrice(priceVal)} TL)`;
          } else {
            return `${a.name} (${qtyVal} Adet - ${formatPrice(priceVal)} TL)`;
          }
        }).join(', ');
        subDetailsLines.push(cleanTurkishChars(`Aksesuarlar: ${accText}`));
      }
    }

    const mainRow = [
      cleanProductType,
      cleanRoomName,
      cleanProductName
    ];

    if (includeMeasurements) {
      mainRow.push(`${order.width || ''} x ${order.height || ''}`);
    }

    const priceText = formatPrice(order.totalPrice || 0);
    const priceCell = order.hasEgimFarki
      ? {
          content: `${priceText}\n${cleanTurkishChars('Egim Farki (+%50)')}`,
          styles: { halign: 'right', valign: 'middle', fontSize: 7.5 }
        }
      : priceText;

    mainRow.push(
      cleanPileModel,
      order.quantity || '1',
      priceCell
    );

    // Ray Sistemi için kullanılan metriği olculer sütununda göster (Müşteri PDF)
    if (order.productType === 'raySistemi' && includeMeasurements) {
      mainRow[3] = `${order.width || ''} cm`; // Boy yok
      const usedMeter = order.usedMeter ? `${order.usedMeter} m` : '-';
      const olcu = order.olcuTamamlama ? ` (${order.olcuTamamlama})` : '';
      mainRow[4] = cleanTurkishChars(order.olcuTamamlama || '-');
      mainRow[3] = `${order.width || ''} cm\n${usedMeter}${olcu}`;
    }

    // Rustik için detayları göster
    if (order.productType === 'rustik' && includeMeasurements) {
      mainRow[3] = `${order.width || ''} cm`; // Boy yok
      const usedCm = order.usedCm ? `${order.usedCm} cm` : '-';
      mainRow[3] = `En: ${order.width || ''} cm\nKul: ${usedCm}`;

      let ayakDetay = cleanTurkishChars(order.ayakModel || '-');
      if (order.ayakSayisi) {
        ayakDetay += `\nAdet: ${order.ayakSayisi}`;
        if (order.ekAyakSayisi) ayakDetay += ` (+${order.ekAyakSayisi} Ek)`;
      }
      if (order.ayakCap || order.ayakRenkKodu) {
        ayakDetay += `\n(${order.ayakCap || ''} ${cleanTurkishChars(order.ayakRenkKodu || '')})`;
      }
      mainRow[4] = ayakDetay;
    }

    const rows = [mainRow];
    if (subDetailsLines.length > 0) {
      const totalCols = includeMeasurements ? 7 : 6;
      rows.push([
        {
          content: subDetailsLines.join('\n'),
          colSpan: totalCols,
          styles: {
            halign: 'left',
            fillColor: [248, 250, 252],
            fontStyle: 'italic',
            textColor: [71, 85, 105],
            fontSize: 8.5,
            cellPadding: 4
          }
        }
      ]);
    }

    return rows;
  });

  return { head, body };
};

// Ana Müşteri PDF oluşturma
const generateCustomerPDFBase = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, options = {}) => {
  const includeMeasurements = options.includeMeasurements !== false;
  const title = options.title || 'Musteri Siparisi';
  const showPaymentInfo = options.showPaymentInfo || false;
  const showDetailedPricing = options.showDetailedPricing !== false; // Varsayılan true
  const paidAmount = options.paidAmount || 0;
  const remainingAmount = options.remainingAmount || 0;

  if (!customerInfo || orders.length === 0) {
    alert('Müşteri bilgileri veya sipariş bulunamadı!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header
  drawHeader(doc, title);

  // Müşteri bilgileri
  let currentY = drawCustomerInfoBox(doc, customerInfo, CONTENT_START_Y);

  // Sipariş tablosu başlığı
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('SIPARIS DETAYLARI', 20, currentY);
  currentY += 8;

  // Tablo verilerini hazırla
  const { head, body } = buildCustomerTableData(orders, includeMeasurements);

  // Tablo oluştur
  autoTable(doc, {
    startY: currentY,
    head,
    body,
    theme: 'striped',
    headStyles: {
      fillColor: PRIMARY_COLOR,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center'
    },
    styles: {
      fontSize: 8.5,
      cellPadding: 2.5,
      halign: 'center',
      valign: 'middle'
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250]
    },
    columnStyles: includeMeasurements
      ? {
        0: { cellWidth: 20 },  // Urun Tipi
        1: { cellWidth: 18 },  // Oda
        2: { cellWidth: 54 },  // Urun Adi (geniş)
        3: { cellWidth: 18 },  // Olculer(cm)
        4: { cellWidth: 20 },  // Pile Modeli
        5: { cellWidth: 12, halign: 'center' },  // Adet
        6: { cellWidth: 28, halign: 'right' }    // Toplam Ucret
      }
      : {
        0: { cellWidth: 22 },  // Urun Tipi
        1: { cellWidth: 20 },  // Oda
        2: { cellWidth: 64 },  // Urun Adi (geniş)
        3: { cellWidth: 22 },  // Pile Modeli
        4: { cellWidth: 12, halign: 'center' },  // Adet
        5: { cellWidth: 30, halign: 'right' }    // Toplam Ucret
      },
    pageBreak: 'auto',
    margin: { top: TABLE_MARGIN_TOP, right: 15, bottom: TABLE_MARGIN_BOTTOM, left: 15 },
    didDrawPage: function (data) {
      if (data.pageNumber > 1) {
        drawHeader(doc, title + ' (Devam)');
      }
    }
  });

  // Toplam tutar kutusu
  let finalY = doc.lastAutoTable.finalY + 10;

  // Toplam kutusunun tahmini yüksekliğini hesapla
  const hasVadeli = vadeliYuzde && parseFloat(vadeliYuzde) > 0;
  let estimatedBoxItems = 1; // Genel toplam
  if (showDetailedPricing) {
    estimatedBoxItems += 1; // Ara toplam
    if (discountedTotalAmount !== totalAmount) estimatedBoxItems++;
    if (vadeliYuzde && parseFloat(vadeliYuzde) > 0) estimatedBoxItems += 2;
    if (genelIskontoYuzde && parseFloat(genelIskontoYuzde) > 0) estimatedBoxItems += 2;
  }
  if (showPaymentInfo) estimatedBoxItems += 2;
  const estimatedBoxHeight = 20 + (estimatedBoxItems * 10);

  // Sayfa kontrolü - footer için 35px, kutu için tahmini yükseklik
  const pageHeight = doc.internal.pageSize.height;
  if (finalY + estimatedBoxHeight > pageHeight - 32) {
    doc.addPage();
    drawHeader(doc, title);
    finalY = CONTENT_START_Y;
  }

  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, finalY, showPaymentInfo, paidAmount, remainingAmount, showDetailedPricing);

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  const fileSuffix = options.fileSuffix || cleanTurkishChars(title).replace(/\s+/g, '_');
  return deliverPdf(doc, buildPdfFileName(customerInfo, fileSuffix));
};

// Müşteri PDF oluşturma
export const generateCustomerPDF = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, paidAmount = 0, remainingAmount = 0) => {
  return generateCustomerPDFBase(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, {
    includeMeasurements: true,
    title: 'Siparis Detayi',
    fileSuffix: 'Siparis_Detayi',
    showPaymentInfo: true,
    showDetailedPricing: false, // Müşteri detayda detaylı fiyat gizle
    paidAmount,
    remainingAmount
  });
};

// Müşteri PDF Blob döndür (WhatsApp paylaşımı için)
export const generateCustomerPDFBlob = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, paidAmount = 0, remainingAmount = 0) => {
  if (!customerInfo || orders.length === 0) return null;
  const doc = new jsPDF();
  doc.setFont('helvetica');
  drawHeader(doc, 'Siparis Detayi');
  let currentY = drawCustomerInfoBox(doc, customerInfo, CONTENT_START_Y);
  doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(...PRIMARY_COLOR);
  doc.text('SIPARIS DETAYLARI', 20, currentY); currentY += 8;
  const { head, body } = buildCustomerTableData(orders, true);
  autoTable(doc, {
    startY: currentY, head, body, theme: 'striped',
    headStyles: { fillColor: PRIMARY_COLOR, textColor: 255, fontStyle: 'bold', fontSize: 9, halign: 'center' },
    styles: { fontSize: 8.5, cellPadding: 2.5, halign: 'center', valign: 'middle' },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: { 0: { cellWidth: 20 }, 1: { cellWidth: 18 }, 2: { cellWidth: 54 }, 3: { cellWidth: 18 }, 4: { cellWidth: 20 }, 5: { cellWidth: 12, halign: 'center' }, 6: { cellWidth: 28, halign: 'right' } },
    pageBreak: 'auto', margin: { top: TABLE_MARGIN_TOP, right: 15, bottom: TABLE_MARGIN_BOTTOM, left: 15 },
    didDrawPage: (data) => { if (data.pageNumber > 1) drawHeader(doc, 'Siparis Detayi (Devam)'); }
  });
  let finalY = doc.lastAutoTable.finalY + 10;
  const pageHeight = doc.internal.pageSize.height;
  if (finalY + 80 > pageHeight - 32) { doc.addPage(); drawHeader(doc, 'Siparis Detayi'); finalY = CONTENT_START_Y; }
  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, finalY, true, paidAmount, remainingAmount, false);
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) { doc.setPage(i); drawFooter(doc); }
  return doc.output('blob');
};

// Müşteri (Sipariş öncesi) PDF oluşturma - en/boy bilgisi olmadan
export const generateCustomerPreOrderPDF = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde) => {
  return generateCustomerPDFBase(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, {
    includeMeasurements: false,
    title: 'Teklif Formu',
    fileSuffix: 'Teklif_Formu',
    showPaymentInfo: false,
    showDetailedPricing: true // Teklif formunda detaylı fiyat göster
  });
};

// Müşteri (Sipariş öncesi) PDF Blob döndür (WhatsApp paylaşımı için)
export const generateCustomerPreOrderPDFBlob = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde) => {
  if (!customerInfo || orders.length === 0) return null;
  const doc = new jsPDF();
  doc.setFont('helvetica');
  drawHeader(doc, 'Teklif Formu');
  let currentY = drawCustomerInfoBox(doc, customerInfo, CONTENT_START_Y);
  doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(...PRIMARY_COLOR);
  doc.text('SIPARIS DETAYLARI', 20, currentY); currentY += 8;
  const { head, body } = buildCustomerTableData(orders, false);
  autoTable(doc, {
    startY: currentY, head, body, theme: 'striped',
    headStyles: { fillColor: PRIMARY_COLOR, textColor: 255, fontStyle: 'bold', fontSize: 9, halign: 'center' },
    styles: { fontSize: 8.5, cellPadding: 2.5, halign: 'center', valign: 'middle' },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: { 0: { cellWidth: 22 }, 1: { cellWidth: 20 }, 2: { cellWidth: 64 }, 3: { cellWidth: 22 }, 4: { cellWidth: 12, halign: 'center' }, 5: { cellWidth: 30, halign: 'right' } },
    pageBreak: 'auto', margin: { top: TABLE_MARGIN_TOP, right: 15, bottom: TABLE_MARGIN_BOTTOM, left: 15 },
    didDrawPage: (data) => { if (data.pageNumber > 1) drawHeader(doc, 'Teklif Formu (Devam)'); }
  });
  let finalY = doc.lastAutoTable.finalY + 10;
  const pageHeight = doc.internal.pageSize.height;
  if (finalY + 60 > pageHeight - 32) { doc.addPage(); drawHeader(doc, 'Teklif Formu'); finalY = CONTENT_START_Y; }
  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, finalY, false, 0, 0, true);
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) { doc.setPage(i); drawFooter(doc); }
  return doc.output('blob');
};

// Atölye PDF oluşturma
export const generateWorkshopPDF = (customerInfo, orders) => {
  if (!customerInfo || orders.length === 0) {
    alert('Müşteri bilgileri veya sipariş bulunamadı!');
    return;
  }

  const tulKanatOrders = orders.filter(order =>
    order.productType === 'tul' || order.productType === 'kanat'
  );

  if (tulKanatOrders.length === 0) {
    alert('Atölye PDF için Tül veya Kanat ürünü bulunamadı!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header - Müşteri adını büyük başlık olarak yazdır (Talep üzerine)
  drawHeader(doc, 'Atolye Siparisi', customerInfo.name);

  // Siparişler için kutular
  const cleanCustomerName = cleanTurkishChars(customerInfo.name);
  let currentY = CONTENT_START_Y;
  let currentX = 12; // Daha geniş alan için kenardan başla
  const boxWidth = 90;
  const margin = 6; // Sütunlar arası boşluğu biraz daralt
  let rowMaxHeight = 0;

  tulKanatOrders.forEach((order) => {
    const cleanNotes = cleanTurkishChars(order.notes || '');
    const extraItems = buildWorkshopOrderExtraLines(order);
    const noteLines = cleanNotes ? splitTextToLines(cleanNotes, 42) : [];
    const boxHeight = calcWorkshopBoxHeight(extraItems, noteLines);

    if (currentY + boxHeight > 270) {
      doc.addPage();
      drawHeader(doc, 'Atolye Siparisi', customerInfo.name);
      currentY = CONTENT_START_Y;
      currentX = 12;
    }

    // Kutu arka planı
    doc.setFillColor(...LIGHT_BG);
    doc.roundedRect(currentX, currentY, boxWidth, boxHeight, 3, 3, 'F');

    // Kutu kenarlığı
    doc.setDrawColor(...PRIMARY_COLOR);
    doc.setLineWidth(0.5);
    doc.roundedRect(currentX, currentY, boxWidth, boxHeight, 3, 3, 'S');

    // Üst şerit - müşteri adı + oda adı
    doc.setFillColor(...PRIMARY_COLOR);
    doc.roundedRect(currentX, currentY, boxWidth, 14, 3, 3, 'F');
    doc.rect(currentX, currentY + 7, boxWidth, 7, 'F'); // Alt köşeleri düzelt

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);

    const cleanRoomName = cleanTurkishChars(order.roomName || '');
    // Sol: Müşteri Adı (Talep üzerine başlık), Sağ: Oda adı
    doc.text(cleanCustomerName.substring(0, 25), currentX + 4, currentY + 9);
    doc.setFontSize(8);
    doc.text(cleanRoomName, currentX + boxWidth - 4, currentY + 9, { align: 'right' });

    // İçerik
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);

    let fullProductName = order.productName || '';
    if (order.productVariant) fullProductName += ` - ${order.productVariant}`;
    const cleanProductName = cleanTurkishChars(fullProductName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');
    const cleanPileModel = cleanTurkishChars(order.pileModel || '');

    doc.text(`Urun: ${cleanProductName.substring(0, 28)}`, currentX + 4, currentY + 20);
    doc.text(`Pile: ${cleanPileModel.substring(0, 25)}`, currentX + 4, currentY + 28);
    doc.setFont('helvetica', 'bold');
    doc.text(`En: ${order.width || '-'} cm  Boy: ${order.height || '-'} cm  Adet: ${order.quantity || '1'}`, currentX + 4, currentY + 36);

    // Ek bilgiler (alt etek katlama, özel tasarım, astar, aksesuar)
    let contentY = currentY + 44;
    if (extraItems.length > 0) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      extraItems.forEach((item) => {
        if (item.spacer) {
          contentY += 5;
          return;
        }
        const textColor = item.color === 'primary' ? PRIMARY_COLOR : [0, 0, 0];
        doc.setTextColor(...textColor);
        doc.setFont('helvetica', item.bold ? 'bold' : 'normal');
        const wrapped = splitTextToLines(item.text, 42);
        wrapped.slice(0, 2).forEach((wrappedLine) => {
          doc.text(wrappedLine, currentX + 4, contentY);
          contentY += 5;
        });
      });
      doc.setTextColor(0, 0, 0);
    }

    // Not alanı — ek bilgilerin altında
    if (cleanNotes) {
      if (extraItems.length > 0) contentY += 5;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY_TEXT);
      doc.text('Not:', currentX + 4, contentY);
      contentY += 5;
      noteLines.slice(0, 3).forEach((line) => {
        doc.text(line, currentX + 4, contentY);
        contentY += 5;
      });
    }

    doc.setTextColor(0, 0, 0);

    // Sonraki kutu pozisyonu
    rowMaxHeight = Math.max(rowMaxHeight, boxHeight);
    currentX += boxWidth + margin;

    if (currentX + boxWidth > 200) {
      currentX = 12;
      currentY += rowMaxHeight + 10;
      rowMaxHeight = 0;
    }
  });

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  return deliverPdf(doc, buildPdfFileName(customerInfo, 'Atolye'));
};

// Montaj PDF oluşturma
export const generateInstallationPDF = (customerInfo, orders) => {
  if (!customerInfo || orders.length === 0) {
    alert('Müşteri bilgileri veya sipariş bulunamadı!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header
  drawHeader(doc, 'Montaj Siparisi');

  // Müşteri bilgileri kutusu
  let currentY = drawCustomerInfoBox(doc, customerInfo, CONTENT_START_Y);

  // Sipariş listesi başlığı
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('MONTAJ LISTESI', 20, currentY);
  currentY += 8;

  // Siparişleri oda gruplarına göre sırala
  const sortedOrders = [...orders].sort((a, b) => {
    const roomGroupA = (a.roomName || '').substring(0, 3).toUpperCase();
    const roomGroupB = (b.roomName || '').substring(0, 3).toUpperCase();

    if (roomGroupA !== roomGroupB) {
      return roomGroupA.localeCompare(roomGroupB, 'tr-TR');
    }

    const roomCompare = (a.roomName || '').localeCompare(b.roomName || '', 'tr-TR');
    if (roomCompare !== 0) return roomCompare;

    return (a.productType || '').localeCompare(b.productType || '', 'tr-TR');
  });

  // Tablo verileri
  const tableData = sortedOrders.flatMap((order) => {
    const cleanProductType = cleanTurkishChars(PRODUCT_TYPES[order.productType]?.label || order.productType || '');
    const cleanRoomName = cleanTurkishChars(order.roomName || '');

    // Plise için detaylı satır bazlı genişletme
    if (order.productType === 'plise' && Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
      const p1Base = cleanTurkishChars(order.pliseProduct1Name || '');
      const p2Base = order.pliseProduct2Name ? ` + ${cleanTurkishChars(order.pliseProduct2Name)}` : '';

      return order.pliseItems.map((item) => {
        const ms = item.multisistem ? ' (Multisistem)' : '';
        const namePart = item.multisistem ? (p1Base + p2Base + ms) : p1Base;
        const qtyPart = ` - ${item.quantity} Adet`;
        const fullProductName = namePart + cleanTurkishChars(qtyPart);
        const olcu = (item.width || item.height)
          ? `${item.width || '-'} x ${item.height || '-'}`
          : '-';

        return [
          cleanRoomName,
          cleanProductType,
          fullProductName,
          olcu
        ];
      });
    }

    // Diğer ürünler için
    let productName = order.productName || '';
    if (order.productVariant) productName += ` - ${order.productVariant}`;
    const cleanProductName = cleanTurkishChars(productName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');

    let olcu = '-';
    if (order.width && order.height) {
      olcu = `${order.width} x ${order.height}`;
    } else if (order.width) {
      olcu = `${order.width}`;
    } else if (order.height) {
      olcu = `${order.height}`;
    }

    return [[
      cleanRoomName,
      cleanProductType,
      cleanProductName,
      olcu
    ]];
  });

  // Tablo oluştur
  autoTable(doc, {
    startY: currentY,
    head: [['Oda', 'Urun Tipi', 'Urun Adi', 'En x Boy (cm)']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: PRIMARY_COLOR,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 10
    },
    styles: {
      fontSize: 10,
      cellPadding: 5
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250]
    },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 35 },
      2: { cellWidth: 70 },
      3: { cellWidth: 30, halign: 'center' }
    },
    margin: { top: TABLE_MARGIN_TOP, right: 15, bottom: TABLE_MARGIN_BOTTOM, left: 15 },
    didDrawPage: function (data) {
      if (data.pageNumber > 1) {
        drawHeader(doc, 'Montaj Siparisi (Devam)');
      }
    }
  });

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  return deliverPdf(doc, buildPdfFileName(customerInfo, 'Montaj'));
};
